import React, { useState } from "react";
import { useLanguage } from "../../hooks/useLanguage";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { submitBooking } from "../../api/bookings";
import { motion } from "framer-motion";

export default function BookingForm() {
  const { t } = useLanguage();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: "2",
    specialRequests: "",
    newsletter: false,
  });
  const [success, setSuccess] = useState(false);

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error(t("booking.errors.nameRequired"));
      return false;
    }
    if (!formData.email.trim()) {
      toast.error(t("booking.errors.emailRequired"));
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error(t("booking.errors.invalidEmail"));
      return false;
    }
    if (!formData.date) {
      toast.error(t("booking.errors.dateRequired"));
      return false;
    }
    if (!formData.time) {
      toast.error(t("booking.errors.timeRequired"));
      return false;
    }
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Log the form data being submitted
      console.log("Submitting booking data:", formData);

      // Add a 1-second delay to ensure UI feedback (spinner) is visible
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const response = await submitBooking(formData);
      console.log("Booking response:", response);

      setSuccess(true);
      toast.success(t("booking.success"));

      // Reset form data after successful submission
      setFormData({
        name: "",
        email: "",
        phone: "",
        date: "",
        time: "",
        guests: "2",
        specialRequests: "",
        newsletter: false,
      });
    } catch (error) {
      console.error("Error submitting booking:", error);

      // Show more specific error message
      if (error instanceof Error) {
        // Format error message to be more user-friendly
        let errorMessage = error.message;

        // Handle specific known errors with custom messages
        if (errorMessage.includes("Database connection")) {
          errorMessage =
            "We're having trouble connecting to our booking system. Please try again in a few minutes.";
        } else if (errorMessage.includes("Failed to store booking")) {
          errorMessage =
            "We couldn't save your booking information. Please try again or contact us directly.";
        }

        toast.error(errorMessage);
      } else {
        toast.error(t("booking.error"));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const value =
      e.target.type === "checkbox"
        ? (e.target as HTMLInputElement).checked
        : e.target.value;
    setFormData({ ...formData, [e.target.name]: value });
  };

  const inputClasses =
    "mt-1 block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white/70 dark:bg-gray-700/70 text-gray-900 dark:text-gray-200 shadow-sm focus:border-primary focus:ring-primary dark:focus:border-secondary dark:focus:ring-secondary hover:border-gray-400 dark:hover:border-gray-500 transition duration-200 ease-in-out";

  if (success) {
    return (
      <motion.div
        className="text-center p-8 bg-green-50/80 dark:bg-green-900/20 rounded-xl backdrop-blur-sm"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center mx-auto mb-4">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-green-600 dark:text-green-400 mb-4">
          {t("booking.success")}
        </h3>
        <p className="text-gray-700 dark:text-gray-300 mb-6">
          {t("booking.successMessage")}
        </p>
        <motion.button
          onClick={() => setSuccess(false)}
          className="bg-gradient-to-r from-primary to-secondary text-white py-2 px-6 rounded-full shadow-md hover:shadow-lg transition-all duration-300"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {t("booking.makeAnother")}
        </motion.button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("booking.name")}
          </label>
          <input
            type="text"
            name="name"
            required
            className={inputClasses}
            value={formData.name}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("booking.email")}
          </label>
          <input
            type="email"
            name="email"
            required
            className={inputClasses}
            value={formData.email}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("booking.phone")}
        </label>
        <input
          type="tel"
          name="phone"
          className={inputClasses}
          value={formData.phone}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("booking.date")}
          </label>
          <input
            type="date"
            name="date"
            required
            min={new Date().toISOString().split("T")[0]}
            className={inputClasses}
            value={formData.date}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("booking.time")}
          </label>
          <input
            type="time"
            name="time"
            required
            className={inputClasses}
            value={formData.time}
            onChange={handleChange}
            disabled={isSubmitting}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {t("booking.guests")}
          </label>
          <select
            name="guests"
            required
            className={inputClasses}
            value={formData.guests}
            onChange={handleChange}
            disabled={isSubmitting}
          >
            {[...Array(10).keys()].map((i) => (
              <option key={i} value={i + 1}>
                {i + 1}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t("booking.specialRequests")}
        </label>
        <textarea
          name="specialRequests"
          rows={4}
          className={inputClasses}
          value={formData.specialRequests}
          onChange={handleChange}
          disabled={isSubmitting}
        />
      </div>

      <div className="flex items-start">
        <div className="flex items-center h-5">
          <input
            id="newsletter"
            name="newsletter"
            type="checkbox"
            checked={formData.newsletter}
            onChange={handleChange}
            disabled={isSubmitting}
            className="focus:ring-primary h-4 w-4 text-primary border-gray-300 rounded"
          />
        </div>
        <div className="ml-3 text-sm">
          <label
            htmlFor="newsletter"
            className="font-medium text-gray-700 dark:text-gray-300"
          >
            {t("booking.newsletter")}
          </label>
          <p className="text-gray-500 dark:text-gray-400">
            {t("booking.newsletterInfo")}
          </p>
        </div>
      </div>

      <motion.button
        type="submit"
        disabled={isSubmitting}
        className={`w-full bg-gradient-to-r from-primary to-secondary text-white py-3 px-6 rounded-full shadow-md transition-all duration-300 ${
          isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:shadow-lg"
        }`}
        whileHover={!isSubmitting ? { scale: 1.02 } : {}}
        whileTap={!isSubmitting ? { scale: 0.98 } : {}}
      >
        {isSubmitting ? (
          <span className="flex items-center justify-center">
            <svg
              className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            {t("booking.submitting")}
          </span>
        ) : (
          t("booking.submit")
        )}
      </motion.button>
      <ToastContainer
        position="top-center"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </form>
  );
}